// EmpathyScroller.js
import React from "react";
import { motion } from "framer-motion";

function WhisperImpact({ text, tone = "down" }) {
  const toneColor =
    tone === "up"
      ? "text-emerald-700 bg-emerald-50/70"
      : "text-rose-700 bg-rose-50/70";
  return (
    <span
      className={[
        "inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold shadow-sm backdrop-blur-sm",
        toneColor,
      ].join(" ")}
    >
      <svg
        viewBox="0 0 20 20"
        className={`mr-1.5 h-3.5 w-3.5 ${tone === "up" ? "rotate-180" : ""}`}
        fill="currentColor"
        aria-hidden="true"
      >
        <path d="M10 17l-6-8h12l-6 8z" />
        <path d="M9 3h2v10H9z" />
      </svg>
      {text}
    </span>
  );
}

function Step({
  i,
  total,
  headline,
  sub,
  impact,
  tone = "down",
  gradient = "from-blue-100/70 via-white to-white",
}) {
  return (
    <section className="relative">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div
          className={`absolute left-1/2 top-0 h-80 w-[90vw] -translate-x-1/2 rounded-[8rem] bg-gradient-to-b ${gradient}`}
        />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.5 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-16"
      >
        <div className="mb-6 text-sm text-slate-500">
          <span className="mr-2 font-medium text-slate-700">Step {i + 1}</span>
          <span className="opacity-70">of {total}</span>
        </div>

        <h3 className="text-balance text-3xl sm:text-5xl md:text-6xl font-semibold leading-[1.05] tracking-tight">
          <span className="bg-gradient-to-r from-slate-800 via-slate-700 to-blue-700 bg-clip-text text-transparent">
            {headline}
          </span>
        </h3>

        {sub && (
          <p className="mt-4 max-w-2xl text-lg text-slate-600">{sub}</p>
        )}

        {impact && (
          <div className="mt-6">
            <div className="sm:float-right sm:translate-y-2">
              <WhisperImpact text={impact} tone={tone} />
            </div>
          </div>
        )}
      </motion.div>
    </section>
  );
}

export default function EmpathyScroller({
  title = "Every data issue has a cost.",
  subtitle =
    "From manufacturing lines to customer operations, finance teams to supply chains — poor data doesn’t stay hidden. It becomes delays, complaints, wasted hours, and broken trust.",
  steps = [
    {
      headline: "Decisions stall because the data doesn’t agree.",
      sub: "Different systems tell different stories. Meetings turn into data arguments instead of decision-making.",
      impact: "Decision cycles +34%",
      tone: "down",
      gradient: "from-blue-100/70 via-white to-white",
    },
    {
      headline: "Operational issues appear long before the root cause is found.",
      sub: "Bad inputs create bad outputs — inefficiencies, wrong orders, incorrect stock, failed automation, and compliance gaps.",
      impact: "Rework +52%",
      tone: "down",
      gradient: "from-cyan-100/70 via-white to-white",
    },
    {
      headline: "Transformation projects slow or fail because the data isn’t ready.",
      sub: "ERP upgrades, CRM replacements, AI adoption, and cloud migrations all rely on clean, trusted, reconciled data.",
      impact: "Go-live delays 3–9 months",
      tone: "down",
      gradient: "from-indigo-100/70 via-white to-white",
    },
  ],
}) {
  return (
    <section id="impacts-story" className="relative py-8">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute inset-0 opacity-[0.04] bg-[linear-gradient(115deg,#000_1px,transparent_1px)] bg-[length:20px_20px]" />
      </div>

      <div className="mx-auto grid max-w-7xl grid-cols-12 gap-6 px-4 sm:px-6 lg:px-8">
        <div className="col-span-1 hidden lg:block">
          <div className="sticky top-28">
            <div className="relative h-[60vh] w-0.5 overflow-hidden rounded-full bg-slate-200/70">
              <div className="absolute inset-0 animate-[flow_2.2s_linear_infinite] bg-[linear-gradient(180deg,transparent,rgba(59,130,246,.35),transparent)]" />
            </div>
            <div className="mt-3 text-[11px] uppercase tracking-wide text-slate-500">
              Scroll
            </div>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-11">
          <div className="mb-8 text-center">
            <h2 className="mt-4 text-3xl font-semibold text-slate-900 sm:text-4xl">
              {title}
            </h2>
            <p className="mt-3 text-slate-600">{subtitle}</p>
          </div>

          {steps.map((s, i) => (
            <Step key={i} i={i} total={steps.length} {...s} />
          ))}

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.6 }}
            transition={{ duration: 0.5 }}
            className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 pb-12"
          >
            <p className="text-center text-slate-600">
              Ready to replace firefighting with clarity?
              <a
                href="#cta"
                className="ml-2 font-semibold text-blue-700 underline decoration-blue-200 underline-offset-4 hover:text-blue-800"
              >
                See how PrismDQ helps
              </a>
            </p>
          </motion.div>
        </div>
      </div>

      <style>{`
        @keyframes flow {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(100%); }
        }
      `}</style>
    </section>
  );
}